/**
 * Onglet « Prévisionnel pluriannuel ».
 *
 * Le simulateur consomme `@scc/core` : la simulation affichée dans le
 * navigateur et le calcul rendu par l'API sont le même code.
 */

import {
  AUDIENS,
  BORNES_SIMULATEUR,
  entier,
  euros,
  eurosSigne,
  keuros,
  keurosValeur,
  nombre,
  normaliserParametres,
  parametresParDefaut,
  pourcent,
  simuler,
  type ParametresSimulateur,
  type Trajectoire,
} from '@scc/core';
import type { Charge } from '../api.js';
import { enregistrerSimulation } from '../api.js';
import { h, vider } from '../dom.js';
import {
  TEINTES,
  graphiqueBarres,
  graphiqueEmpile,
  graphiqueLignes,
  graphiquePointMort,
  legende,
  teinte,
} from '../graphiques.js';
import { badge, barreExport, enteteSection, insight, kpi, noteLecture } from '../composants.js';

const COULEURS: Record<string, string> = {
  sc1: TEINTES.slate,
  sc2: TEINTES.green,
  sc3: TEINTES.amber,
  sim: TEINTES.rose,
};

interface Curseur {
  readonly cle: keyof ParametresSimulateur;
  readonly libelle: string;
  readonly bornes: { min: number; max: number; pas: number };
  readonly format: (v: number) => string;
  readonly aide: (d: Charge) => string;
}

function curseurs(d: Charge): Curseur[] {
  const v = d.previsionnelVote.produitsParPoste;
  const a = d.adherents;
  const derniere = a.seriesClotures[a.seriesClotures.length - 1];
  return [
    {
      cle: 'cible',
      libelle: 'Adhérents visés en 2030',
      bornes: BORNES_SIMULATEUR.cible,
      format: (x) => entier(x),
      aide: () =>
        `${entier(derniere?.payeurs ?? 0)} adhérents payeurs en ${derniere?.annee ?? ''}, ` +
        `environ ${entier(a.payeursProjetes)} attendus en ${d.meta.exerciceCourant}.`,
    },
    {
      cle: 'cotisationMoyenne',
      libelle: 'Cotisation moyenne',
      bornes: BORNES_SIMULATEUR.cotisationMoyenne,
      format: euros,
      aide: () =>
        `${euros(a.produitMoyenImplicite)} implicites en ${d.meta.exerciceCourant} ; ` +
        `${euros(derniere?.produitMoyen ?? 0)} constatés en ${derniere?.annee ?? ''}.`,
    },
    {
      cle: 'dgca',
      libelle: 'Subventions publiques DGCA',
      bornes: BORNES_SIMULATEUR.dgca,
      format: euros,
      aide: () =>
        `${euros(v['dgca_professionnalisation'] ?? 0)} Professionnalisation et ${euros(v['dgca_theatre'] ?? 0)} Théâtre ` +
        `en ${d.meta.exerciceCourant}.`,
    },
    {
      cle: 'fcapCcneac',
      libelle: 'FCAP CCNEAC',
      bornes: BORNES_SIMULATEUR.fcapCcneac,
      format: euros,
      aide: () => {
        const p = d.execution.produitsParPoste.find((x) => x.poste === 'fcap_ccneac');
        return `${euros(v['fcap_ccneac'] ?? 0)} budgétés, dont ${euros(p?.realise ?? 0)} déjà encaissés.`;
      },
    },
    {
      cle: 'fcapCcnsvp',
      libelle: 'FCAP CCNSVP',
      bornes: BORNES_SIMULATEUR.fcapCcnsvp,
      format: euros,
      aide: () => {
        const p = d.execution.produitsParPoste.find((x) => x.poste === 'fcap_ccnsvp');
        return `${euros(v['fcap_ccnsvp'] ?? 0)} budgétés, ${euros(p?.realise ?? 0)} encaissés à ce jour.`;
      },
    },
    {
      cle: 'audiens',
      libelle: 'Convention Audiens',
      bornes: BORNES_SIMULATEUR.audiens,
      format: euros,
      aide: () =>
        `Convention arrivant à échéance en ${AUDIENS.derniereAnnee} : ce montant n'est retenu que ` +
        `jusqu'à cet exercice, puis ramené à zéro.`,
    },
    {
      cle: 'subventionPonctuelle',
      libelle: 'Subvention ponctuelle',
      bornes: BORNES_SIMULATEUR.subventionPonctuelle,
      format: (x) => (x === 0 ? 'aucune' : euros(x)),
      aide: () => 'Ressource nouvelle non reconduite, par exemple une aide régionale.',
    },
    {
      cle: 'progressionCharges',
      libelle: 'Progression annuelle des charges',
      bornes: BORNES_SIMULATEUR.progressionCharges,
      format: (x) => pourcent(x),
      aide: () => `Base ${euros(d.projection.chargesProjetees)} au niveau réel ${d.meta.exerciceCourant}.`,
    },
    {
      cle: 'ajustement',
      libelle: 'Économie ou dépense nouvelle par an',
      bornes: BORNES_SIMULATEUR.ajustement,
      format: (x) => (x === 0 ? euros(0) : eurosSigne(x)),
      aide: () => 'Valeur négative pour une économie structurelle, positive pour une charge nouvelle.',
    },
  ];
}

export function vuePrevisionnel(d: Charge, initiaux?: Partial<ParametresSimulateur>): HTMLElement {
  const annees = d.trajectoires[0]?.annees.map((a) => a.annee) ?? [];
  const anneesAvecDepart = [d.cloture.exercice, ...annees];
  const seuils = [d.previsionnelVote.reservePrevueFinExercicePrecedent, ...(d.trajectoires[0]?.annees.map((a) => a.seuil) ?? [])];
  const seuilsReels = [
    (d.trajectoires[0]?.annees[0]?.seuil ?? 0) - 2569.1,
    ...(d.trajectoires[0]?.annees.map((a) => a.seuil) ?? []),
  ];
  void seuils;

  let params: ParametresSimulateur = normaliserParametres(initiaux ?? {});
  const entreesSim = {
    payeursExerciceCourant: d.adherents.payeursProjetes,
    produitsExerciceCourant: d.projection.produitsProjetes,
    chargesExerciceCourant: d.projection.chargesProjetees,
    reserveDepart: d.chainage.fondsPropresCloture,
  };

  const conteneurGraphiqueReserve = h('div', {});
  const conteneurKpiScenario = h('div', { classe: 'kpi-grid' });
  const conteneurTableScenario = h('div', {});
  const conteneurKpiSim = h('div', { classe: 'kpi-grid' });
  const conteneurGraphiqueSim = h('div', {});
  const conteneurMessage = h('div', {});
  const conteneurLien = h('div', { classe: 'barre-actions' });

  let scenarioActif = 'sc1';

  const trajectoires = (): Trajectoire[] => [
    ...d.trajectoires.filter((t) => t.cle !== 'sim'),
    simuler(params, entreesSim),
  ];

  const rendreGraphiqueReserve = (): void => {
    const ts = trajectoires();
    vider(conteneurGraphiqueReserve);
    conteneurGraphiqueReserve.appendChild(
      graphiqueLignes(
        anneesAvecDepart,
        ts.map((t) => ({
          nom: t.libelle,
          couleur: COULEURS[t.cle] ?? TEINTES.slate,
          valeurs: [d.chainage.fondsPropresCloture, ...t.annees.map((a) => a.reserve)],
          epaisseur: t.cle === 'sim' ? 3 : 2.5,
        })),
        {
          bande: seuilsReels,
          nomBande: 'Seuil de sécurité',
          depuisZero: true,
          titre: `Trajectoire des réserves ${d.cloture.exercice}-${annees[annees.length - 1] ?? ''}`,
          enteteAbscisse: 'Exercice',
        },
      ),
    );
    conteneurGraphiqueReserve.appendChild(
      legende([
        ...ts.map((t) => ({ nom: t.libelle, couleur: COULEURS[t.cle] ?? TEINTES.slate })),
        { nom: 'Seuil de sécurité', couleur: TEINTES.danger },
      ]),
    );
  };

  const rendreScenario = (): void => {
    const t = trajectoires().find((x) => x.cle === scenarioActif);
    if (!t) return;
    const derniere = t.annees[t.annees.length - 1];

    vider(conteneurKpiScenario);
    conteneurKpiScenario.appendChild(
      kpi(t.resultatCumule >= 0 ? 'progress' : 'alert', keurosValeur(t.resultatCumule), 'k€', `Résultat cumulé ${annees[0]}-${annees[annees.length - 1]}`, {
        sens: t.resultatCumule >= 0 ? 'up' : 'down',
        texte: `${t.resultatCumule >= 0 ? '▲' : '▼'} sur ${annees.length} exercices`,
      }),
    );
    conteneurKpiScenario.appendChild(
      kpi(
        t.reserveFinale > (derniere?.seuil ?? 0) * 2 ? 'progress' : t.reserveFinale > (derniere?.seuil ?? 0) ? 'warning' : 'alert',
        keurosValeur(t.reserveFinale),
        'k€',
        `Réserve au 31 décembre ${annees[annees.length - 1]}`,
        {
          sens: t.reserveFinale >= d.chainage.fondsPropresCloture ? 'up' : 'down',
          texte: `${eurosSigne(t.reserveFinale - d.chainage.fondsPropresCloture)} vs ${d.cloture.exercice}`,
        },
      ),
    );
    conteneurKpiScenario.appendChild(
      kpi(
        t.nbExercicesDeficitaires === 0 ? 'progress' : t.nbExercicesDeficitaires <= 2 ? 'warning' : 'alert',
        `${t.nbExercicesDeficitaires}`,
        `/ ${annees.length}`,
        'Exercices déficitaires',
        { sens: t.nbExercicesDeficitaires === 0 ? 'up' : 'down', texte: t.nbExercicesDeficitaires === 0 ? '✦ aucun déficit' : '▼ sur la période' },
      ),
    );
    conteneurKpiScenario.appendChild(
      kpi(
        t.franchissementSeuil ? 'alert' : 'progress',
        t.franchissementSeuil ? `${t.franchissementSeuil}` : 'Aucun',
        null,
        'Franchissement du seuil de sécurité',
        { sens: t.franchissementSeuil ? 'down' : 'up', texte: t.franchissementSeuil ? '▼ réserve insuffisante' : '✦ seuil préservé' },
      ),
    );

    vider(conteneurTableScenario);
    conteneurTableScenario.appendChild(
      h(
        'table',
        { classe: 'comp-table' },
        h('caption', { classe: 'invisible' }, `Détail annuel — ${t.libelle}`),
        h(
          'thead',
          {},
          h(
            'tr',
            {},
            h('th', { scope: 'col' }, 'Exercice'),
            h('th', { scope: 'col' }, 'Adhérents'),
            h('th', { scope: 'col' }, 'Produits'),
            h('th', { scope: 'col' }, 'Charges'),
            h('th', { scope: 'col' }, 'Résultat'),
            h('th', { scope: 'col' }, 'Réserve fin d’année'),
          ),
        ),
        h(
          'tbody',
          {},
          ...t.annees.map((a) =>
            h(
              'tr',
              {},
              h('th', { scope: 'row' }, h('strong', {}, `${a.annee}`)),
              h('td', {}, entier(a.adherents)),
              h('td', { classe: 'num' }, euros(a.produits)),
              h('td', { classe: 'num' }, euros(a.charges)),
              h('td', { classe: 'num' }, badge(a.resultat >= 0 ? 'favorable' : 'alerte', eurosSigne(a.resultat))),
              h('td', { classe: 'num' }, euros(a.reserve)),
            ),
          ),
        ),
      ),
    );
  };

  const rendreSimulateur = (): void => {
    const t = simuler(params, entreesSim);
    const derniere = t.annees[t.annees.length - 1];

    vider(conteneurKpiSim);
    conteneurKpiSim.appendChild(
      kpi(
        t.reserveFinale > d.chainage.reserveAttendue ? 'progress' : t.reserveFinale > (derniere?.seuil ?? 0) ? 'warning' : 'alert',
        keurosValeur(t.reserveFinale),
        'k€',
        `Réserve au 31 décembre ${annees[annees.length - 1]}`,
        {
          sens: t.reserveFinale >= d.chainage.reserveAttendue ? 'up' : 'down',
          texte: `${eurosSigne(t.reserveFinale - d.chainage.reserveAttendue)} vs fin ${d.meta.exerciceCourant}`,
        },
      ),
    );
    conteneurKpiSim.appendChild(
      kpi(t.resultatCumule >= 0 ? 'progress' : 'alert', keurosValeur(t.resultatCumule), 'k€', `Résultat cumulé ${annees[0]}-${annees[annees.length - 1]}`, {
        sens: t.resultatCumule >= 0 ? 'up' : 'down',
        texte: `dont ${keuros(d.projection.resultatProjete)} en ${d.meta.exerciceCourant}`,
      }),
    );
    conteneurKpiSim.appendChild(
      kpi(t.retourEquilibre ? 'progress' : 'warning', t.retourEquilibre ? `${t.retourEquilibre}` : 'Jamais', null, 'Retour à l’équilibre annuel', {
        sens: t.retourEquilibre ? 'up' : 'down',
        texte: t.retourEquilibre ? '✦ premier exercice positif' : '▼ aucun sur la période',
      }),
    );
    conteneurKpiSim.appendChild(
      kpi(t.franchissementSeuil ? 'alert' : 'progress', t.franchissementSeuil ? `${t.franchissementSeuil}` : 'Aucun', null, 'Franchissement du seuil de sécurité', {
        sens: t.franchissementSeuil ? 'down' : 'up',
        texte: t.franchissementSeuil ? '▼ réserve insuffisante' : '✦ seuil préservé',
      }),
    );

    vider(conteneurGraphiqueSim);
    conteneurGraphiqueSim.appendChild(
      graphiqueBarres(
        annees,
        { nom: 'Produits', couleur: TEINTES.blue, valeurs: t.annees.map((a) => a.produits) },
        { nom: 'Charges', couleur: TEINTES.danger, valeurs: t.annees.map((a) => a.charges) },
        'Produits et charges simulés',
      ),
    );
    conteneurGraphiqueSim.appendChild(
      legende([
        { nom: 'Produits', couleur: TEINTES.blue },
        { nom: 'Charges', couleur: TEINTES.danger },
      ]),
    );

    vider(conteneurMessage);
    const ecart = t.reserveFinale - d.chainage.reserveAttendue;
    if (t.franchissementSeuil) {
      conteneurMessage.appendChild(
        insight(
          'negative',
          '⚠',
          `**Seuil de sécurité franchi en ${t.franchissementSeuil} :** avec ${entier(params.cible)} adhérents en ` +
            `${annees[annees.length - 1]} et une cotisation moyenne de ${euros(params.cotisationMoyenne)}, la réserve ` +
            `tombe à ${euros(t.reserveFinale)}. Le syndicat ne serait plus en mesure d'absorber une rupture du contrat ` +
            `de direction.`,
        ),
      );
    } else if (t.retourEquilibre) {
      conteneurMessage.appendChild(
        insight(
          'positive',
          '✦',
          `**Équilibre retrouvé en ${t.retourEquilibre} :** avec ${entier(params.cible)} adhérents en ` +
            `${annees[annees.length - 1]} et une cotisation moyenne de ${euros(params.cotisationMoyenne)}, la réserve ` +
            `s'établit à ${euros(t.reserveFinale)}, soit ${eurosSigne(ecart)} par rapport à fin ${d.meta.exerciceCourant}.`,
        ),
      );
    } else {
      conteneurMessage.appendChild(
        insight(
          'warning',
          '⚠',
          `**Trajectoire déficitaire mais soutenable :** avec ${entier(params.cible)} adhérents en ` +
            `${annees[annees.length - 1]} et une cotisation moyenne de ${euros(params.cotisationMoyenne)}, aucun ` +
            `exercice ne revient à l'équilibre. La réserve s'établit à ${euros(t.reserveFinale)}, au-dessus du seuil ` +
            `de sécurité mais en recul de ${euros(Math.abs(ecart))}.`,
        ),
      );
    }

    rendreGraphiqueReserve();
    if (scenarioActif === 'sim') rendreScenario();
  };

  // ── Curseurs ──────────────────────────────────────────────────────────
  const liste = curseurs(d);
  const valeursAffichees = new Map<string, HTMLElement>();
  const entrees = new Map<string, HTMLInputElement>();

  const grille = h(
    'div',
    { classe: 'controls' },
    ...liste.map((c) => {
      const idc = `curseur-${c.cle}`;
      const valeur = h('b', {}, c.format(params[c.cle] as number));
      valeursAffichees.set(c.cle, valeur);
      const input = h('input', {
        type: 'range',
        id: idc,
        min: c.bornes.min,
        max: c.bornes.max,
        step: c.bornes.pas,
        value: String(params[c.cle]),
        surInput: (e) => {
          const v = Number((e.target as HTMLInputElement).value);
          params = normaliserParametres({ ...params, [c.cle]: v });
          valeur.textContent = c.format(params[c.cle] as number);
          rendreSimulateur();
        },
      });
      entrees.set(c.cle, input);
      return h(
        'div',
        { classe: 'ctrl' },
        h('label', { for: idc }, c.libelle, valeur),
        input,
        h('p', { classe: 'hint' }, c.aide(d)),
      );
    }),
    (() => {
      const sel = h(
        'select',
        {
          classe: 'ctrl-select',
          id: 'curseur-exercice-subvention',
          surChange: (e) => {
            const v = (e.target as HTMLSelectElement).value;
            params = normaliserParametres({ ...params, exerciceSubvention: v === 'all' ? 'all' : Number(v) });
            rendreSimulateur();
          },
        },
        ...annees
          .filter((a) => a > d.meta.exerciceCourant)
          .map((a) => h('option', { value: `${a}`, selected: params.exerciceSubvention === a }, `${a} seulement`)),
        h('option', { value: 'all', selected: params.exerciceSubvention === 'all' }, `Chaque exercice de ${annees[1]} à ${annees[annees.length - 1]}`),
      );
      return h(
        'div',
        { classe: 'ctrl' },
        h('label', { for: 'curseur-exercice-subvention' }, 'Exercice de la subvention ponctuelle'),
        sel,
        h('p', { classe: 'hint' }, `L'exercice ${d.meta.exerciceCourant} est figé sur la projection de clôture et n'est pas affecté.`),
      );
    })(),
  );

  const reinitialiser = (): void => {
    params = parametresParDefaut();
    for (const c of liste) {
      const input = entrees.get(c.cle);
      if (input) input.value = String(params[c.cle]);
      const v = valeursAffichees.get(c.cle);
      if (v) v.textContent = c.format(params[c.cle] as number);
    }
    rendreSimulateur();
  };

  const champNom = h('input', {
    type: 'text',
    id: 'nom-simulation',
    placeholder: 'Nom du jeu de paramètres',
    classe: 'ctrl-select',
  });

  const enregistrer = async (): Promise<void> => {
    const nom = champNom.value.trim();
    vider(conteneurLien);
    if (nom === '') {
      conteneurLien.appendChild(h('span', { classe: 'titre' }, 'Indiquez un nom avant d’enregistrer.'));
      return;
    }
    try {
      const r = await enregistrerSimulation(nom, params);
      const url = `${window.location.origin}${r.lien}`;
      conteneurLien.appendChild(h('span', { classe: 'titre' }, 'Lien de partage'));
      conteneurLien.appendChild(h('a', { classe: 'btn-action', href: r.lien }, url));
    } catch {
      conteneurLien.appendChild(h('span', { classe: 'titre' }, 'Enregistrement refusé.'));
    }
  };

  const ongletsScenario = h(
    'div',
    { classe: 'tabs', role: 'tablist', 'aria-label': 'Scénarios' },
    ...[...d.trajectoires.filter((t) => t.cle !== 'sim'), { cle: 'sim', libelle: 'Simulateur' }].map((t) =>
      h(
        'button',
        {
          classe: 'tab',
          type: 'button',
          role: 'tab',
          'aria-selected': t.cle === scenarioActif ? 'true' : 'false',
          surClick: (e) => {
            scenarioActif = t.cle;
            for (const b of ongletsScenario.querySelectorAll('[role="tab"]')) {
              b.setAttribute('aria-selected', b === e.currentTarget ? 'true' : 'false');
            }
            rendreScenario();
          },
        },
        t.libelle,
      ),
    ),
  );

  rendreGraphiqueReserve();
  rendreScenario();
  rendreSimulateur();

  // ── Structure et point mort ─────────────────────────────────────────────
  //
  // Les deux graphiques répondent à des questions distinctes : l'empilement
  // montre comment la structure se déforme, le point mort ce qu'il faudrait
  // d'adhérents pour l'équilibrer. Ils partagent le même scénario.
  const zoneStructure = h('div', {});
  let cleStructure = d.trajectoires.find((t) => t.cle === 'sc3')?.cle ?? d.trajectoires[0]?.cle ?? '';

  const dessinerStructure = (): void => {
    vider(zoneStructure);
    const budget = d.budgets.find((b) => b.scenario === cleStructure);
    const pm = d.pointsMorts.find((x) => x.cle === cleStructure);
    if (!budget) return;

    const exercicesBudget = budget.exercices.map((x) => x.exercice);
    const categoriesCharges = budget.totauxParCategorie.filter((c) => c.nature === 'charge');
    const categoriesProduits = budget.totauxParCategorie.filter((c) => c.nature === 'produit');

    zoneStructure.appendChild(
      graphiqueEmpile(
        exercicesBudget,
        categoriesProduits.map((c, i) => ({
          nom: c.categorie,
          couleur: teinte(i),
          valeurs: c.parExercice,
        })),
        { titre: 'Produits par catégorie', enteteAbscisse: 'Exercice' },
      ),
    );
    zoneStructure.appendChild(
      legende(categoriesProduits.map((c, i) => ({ nom: c.categorie, couleur: teinte(i) }))),
    );
    zoneStructure.appendChild(
      graphiqueEmpile(
        exercicesBudget,
        categoriesCharges.map((c, i) => ({
          nom: c.categorie,
          couleur: teinte(i + categoriesProduits.length),
          valeurs: c.parExercice,
        })),
        { titre: 'Charges par catégorie', enteteAbscisse: 'Exercice' },
      ),
    );
    zoneStructure.appendChild(
      legende(
        categoriesCharges.map((c, i) => ({
          nom: c.categorie,
          couleur: teinte(i + categoriesProduits.length),
        })),
      ),
    );

    // Point mort du premier exercice projeté : c'est celui qui se décide.
    const point = pm?.points[0];
    if (point) {
      zoneStructure.appendChild(
        graphiquePointMort({
          titre: `Point mort ${point.exercice} — ${pm?.libelle ?? ''}`,
          exercice: point.exercice,
          charges: point.charges,
          produitsFixes: point.produitsFixes,
          produitMoyen: point.produitMoyen,
          effectifProjete: point.effectifProjete,
          adherentsPointMort: point.adherents,
          indetermine: point.indetermine,
        }),
      );
      zoneStructure.appendChild(
        insight(
          point.ecart > 0 ? 'warning' : 'positive',
          point.ecart > 0 ? '⚠' : '✓',
          `**Point mort ${point.exercice}.** À produit moyen de ${euros(point.produitMoyen)} et ` +
            `${euros(point.produitsFixes)} de ressources indépendantes de l'effectif, il faut ` +
            `${entier(point.adherents)} adhérents payeurs pour équilibrer ${euros(point.charges)} de charges. ` +
            (point.ecart > 0
              ? `Le scénario en retient ${entier(point.effectifProjete)} : il en manque ${entier(point.ecart)}.`
              : `Le scénario en retient ${entier(point.effectifProjete)}, soit ${entier(-point.ecart)} de plus qu'il n'en faut.`),
        ),
      );
    }

    // Le point mort de chaque exercice de la période, en un coup d'œil.
    if (pm) {
      zoneStructure.appendChild(
        h(
          'table',
          { classe: 'comp-table encadre-tableau' },
          h('caption', { classe: 'invisible' }, 'Point mort par exercice'),
          h(
            'thead',
            {},
            h(
              'tr',
              {},
              h('th', { scope: 'col' }, 'Exercice'),
              h('th', { scope: 'col' }, 'Charges à couvrir'),
              h('th', { scope: 'col' }, 'Produits fixes'),
              h('th', { scope: 'col' }, 'Produit moyen'),
              h('th', { scope: 'col' }, 'Adhérents à l’équilibre'),
              h('th', { scope: 'col' }, 'Effectif retenu'),
              h('th', { scope: 'col' }, 'Écart'),
            ),
          ),
          h(
            'tbody',
            {},
            ...pm.points.map((p) =>
              h(
                'tr',
                { classe: p.ecart > 0 ? 'ligne-alerte' : '' },
                h('th', { scope: 'row' }, String(p.exercice)),
                h('td', { classe: 'num' }, euros(p.charges)),
                h('td', { classe: 'num' }, euros(p.produitsFixes)),
                h('td', { classe: 'num' }, euros(p.produitMoyen)),
                h('td', { classe: 'num' }, entier(p.adherents)),
                h('td', { classe: 'num' }, entier(p.effectifProjete)),
                h('td', { classe: 'num' }, p.ecart > 0 ? `+${entier(p.ecart)}` : entier(p.ecart)),
              ),
            ),
          ),
        ),
      );
    }
  };

  const selecteurStructure = h(
    'div',
    { classe: 'barre-actions' },
    h('span', { classe: 'titre' }, 'Scénario'),
    h(
      'select',
      {
        'aria-label': 'Scénario pour la structure et le point mort',
        surChange: (ev) => {
          cleStructure = (ev.currentTarget as HTMLSelectElement).value;
          dessinerStructure();
        },
      },
      ...d.budgets.map((b) =>
        h('option', { value: b.scenario, ...(b.scenario === cleStructure ? { selected: true } : {}) }, b.libelle),
      ),
    ),
  );

  dessinerStructure();

  return h(
    'div',
    { classe: 'page', id: 'page-trajectoires', role: 'region', 'aria-label': 'Trajectoires et simulateur' },
    h(
      'section',
      {},
      enteteSection(1, `La trajectoire des réserves jusqu'en ${annees[annees.length - 1]}`, `Base actualisée : fonds propres réels au 31 décembre ${d.cloture.exercice}`),
      h(
        'p',
        { classe: 'text-block' },
        `Les courbes partent du même point, les ${euros(d.chainage.fondsPropresCloture)} de fonds propres constatés à ` +
          `la clôture ${d.cloture.exercice}. La bande rouge marque le seuil en deçà duquel le syndicat ne pourrait plus ` +
          `faire face à la rupture du contrat de direction tout en conservant son plancher de trésorerie.`,
      ),
      conteneurGraphiqueReserve,
    ),
    h('hr', { classe: 'divider' }),
    h(
      'section',
      {},
      enteteSection(2, 'Les scénarios ligne à ligne', 'Sélectionner un scénario pour afficher le détail annuel'),
      ongletsScenario,
      conteneurKpiScenario,
      conteneurTableScenario,
      noteLecture(
        `Note de lecture : les réserves sont recalculées à partir des fonds propres réels au 31 décembre ` +
          `${d.cloture.exercice} (${euros(d.chainage.fondsPropresCloture)}) et non de la projection du classeur ` +
          `d'origine. Le scénario 3 est construit à partir des données réelles et ne figure pas au classeur.`,
      ),
      h(
        'div',
        { classe: 'barre-actions' },
        h('span', { classe: 'titre' }, 'Exporter'),
        ...d.trajectoires
          .filter((t) => t.cle !== 'sim')
          .map((t) => h('a', { classe: 'btn-action', href: `/api/exports/trajectoire-${t.cle}.xlsx`, download: '' }, t.libelle)),
      ),
    ),
    h('hr', { classe: 'divider' }),
    h(
      'section',
      {},
      enteteSection(
        3,
        'Structure et point mort',
        'Comment se déforme le budget, et combien d’adhérents il faudrait pour l’équilibrer',
      ),
      selecteurStructure,
      zoneStructure,
    ),
    h('hr', { classe: 'divider' }),
    h(
      'section',
      {},
      enteteSection(4, 'Simulateur : quelles hypothèses pour tenir la trajectoire', `Les curseurs pilotent ${annees[1]} à ${annees[annees.length - 1]} ; ${d.meta.exerciceCourant} reste la projection réelle`),
      h(
        'p',
        { classe: 'text-block' },
        `Le point de départ est la réserve attendue au 31 décembre ${d.meta.exerciceCourant}, soit ` +
          `${euros(d.chainage.reserveAttendue)} après le résultat projeté de ${eurosSigne(d.projection.resultatProjete)}. ` +
          `Les effectifs sont interpolés linéairement entre ${entier(d.adherents.payeursProjetes)} adhérents payeurs en ` +
          `${d.meta.exerciceCourant} et la cible retenue pour ${annees[annees.length - 1]}.`,
      ),
      h(
        'div',
        { classe: 'card' },
        h(
          'div',
          { classe: 'barre-actions' },
          h('h3', {}, 'Hypothèses ajustables'),
          h('button', { classe: 'btn-reset', type: 'button', surClick: reinitialiser }, 'Réinitialiser'),
        ),
        grille,
      ),
      conteneurKpiSim,
      conteneurGraphiqueSim,
      noteLecture(
        `Note de lecture : produits en bleu, charges en rouge. L'exercice ${d.meta.exerciceCourant} correspond à la ` +
          `projection de clôture et n'est pas piloté par les curseurs. La convention Audiens n'est comptée que jusqu'en ` +
          `${AUDIENS.derniereAnnee}.`,
      ),
      conteneurMessage,
      h(
        'div',
        { classe: 'card' },
        h('h3', {}, 'Enregistrer ce jeu de paramètres'),
        h(
          'div',
          { classe: 'barre-actions' },
          champNom,
          h('button', { classe: 'btn-action', type: 'button', surClick: () => void enregistrer() }, 'Enregistrer et partager'),
        ),
        conteneurLien,
        noteLecture(
          'Le lien ouvre la simulation avec ces paramètres : un élu peut retrouver la simulation présentée en séance.',
        ),
      ),
      barreExport('trajectoire-sc3', 'Trajectoire de référence'),
      noteLecture(`Pente du produit moyen retenue : ${nombre(d.adherents.regression.pente, 2)} € par an.`),
    ),
  );
}
